/* eslint-disable react/prop-types */
import React, { useEffect, useContext, useState } from 'react';

import GridItem from 'components/Grid/GridItem.js';
import { Button, useMediaQuery } from '@mui/material';
import AuthenticatedContainer from 'components/AuthenticatedContainer';
import CircularProgress from '@mui/material/CircularProgress';
import UserService from '../../services/UserService';
import makeStyles from '@mui/styles/makeStyles';
import { Context } from 'Store';
import types from 'Reducer/types';
import useAPI from 'useAPI';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import TermsOfUseContent from './TermsOfUseContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import mixpanel from 'mixpanel-browser';
import { PAGE_TOP_PADDING } from './paginatePolicy';
import usePolicyPagination from './usePolicyPagination';

const useStyles = makeStyles(() => ({
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

function PaginatedTerms({ onAgree, onDisagree, saving }) {
  const classes = useStyles();
  const {
    outerRef,
    measureRef,
    displayRef,
    footerRef,
    navigationRef,
    finalActionsRef,
    totalPages,
    currentPage,
    setCurrentPage,
    isLastPage,
  } = usePolicyPagination();
  const [checked, setChecked] = useState(false);

  return (
    <Box
      ref={outerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Hidden measurement container — same width, used to measure block heights */}
      <Box
        ref={measureRef}
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          top: 0,
          left: 0,
          right: 0,
          height: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        <TermsOfUseContent />
      </Box>

      {/* Visible display — blocks hidden/shown via DOM */}
      <Box
        ref={displayRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          pt: `${PAGE_TOP_PADDING}px`,
        }}
      >
        <TermsOfUseContent />
      </Box>

      {/* Navigation bar */}
      <Box
        ref={footerRef}
        sx={{
          flexShrink: 0,
          position: 'relative',
          bgcolor: 'background.paper',
          pb: 'env(safe-area-inset-bottom, 0px)',
          '& .MuiButton-root': { minHeight: 44 },
        }}
      >
        <Box
          ref={navigationRef}
          sx={{
            px: 2,
            py: 1,
            borderTop: '1px solid #e0e0e0',
          }}
        >
          {/* Back / Page / Next row */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: 44,
              gap: 1,
            }}
          >
            <Box>
              {currentPage > 0 && (
                <Button onClick={() => setCurrentPage((p) => p - 1)}>Back</Button>
              )}
            </Box>

            {totalPages > 1 && (
              <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center' }}>
                Page {currentPage + 1} of {totalPages}
              </Typography>
            )}

            {totalPages > 0 && !isLastPage && (
              <Button onClick={() => setCurrentPage((p) => p + 1)}>{'Next ->'}</Button>
            )}

            {isLastPage && <Box />}
          </Box>
        </Box>

        {/* Checkbox + Agree/Disagree (last page only) */}
        <Box
          ref={finalActionsRef}
          aria-hidden={!isLastPage}
          sx={{
            px: 2,
            pb: 1,
            // Keep a measurable copy without reserving space on earlier pages.
            position: isLastPage ? 'relative' : 'absolute',
            visibility: isLastPage ? 'visible' : 'hidden',
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                color='primary'
                disabled={!isLastPage}
              />
            }
            label='I have read the Terms of Use'
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Button onClick={() => onDisagree()} disabled={!isLastPage || !checked}>
              Disagree
            </Button>
            <div style={{ position: 'relative' }}>
              <Button onClick={() => onAgree()} color='primary' disabled={!isLastPage || !checked}>
                Agree
              </Button>
              {saving && <CircularProgress size={14} className={classes.buttonProgress} />}
            </div>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default function TNCDialogBox() {
  const fullScreen = useMediaQuery('(max-width: 600px), (max-height: 500px)');
  const api = useAPI();
  const { state, dispatch } = useContext(Context);
  const { tncChange, user } = state;
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    UserService.init(api);
  }, [api]);

  useEffect(() => {
    if (user && !user.is_new) {
      setStep(2);
    }
  }, [user]);

  function isUserNeedToAgree() {
    return Object.keys(user).length > 0 && !user.is_agree;
  }

  function isShowRegistrationSuccessful() {
    return isUserNeedToAgree() && step === 1;
  }

  const gotoStep2 = () => {
    mixpanel.track('REGISTRATION_SUCCESSFUL');
    setStep(2);
  };

  function isShowTermsAndConditions() {
    return isUserNeedToAgree() && step === 2;
  }

  const handleAgreeToTermsAndConditions = (agree) => {
    mixpanel.track(agree ? 'TNC_AGREE' : 'TNC_DISAGREE');
    setSaving(true);
    UserService.updateTermsAndConditions(user, agree).then((user) => {
      dispatch({ type: types.SET_USER, payload: user });
      dispatch({
        type: types.SET_TNC_CHANGE,
        payload: !tncChange,
      });
    });
    setSaving(false);
  };

  return (
    <AuthenticatedContainer>
      <GridItem xs={12} sm={12} md={8} align='center'>
        {/* Step 1 - Thanks for registering */}
        <Dialog
          open={isShowRegistrationSuccessful()}
          aria-labelledby='alert-dialog-title'
          aria-describedby='alert-dialog-description'
        >
          <DialogTitle id='alert-dialog-title'>{'Registration Successful'}</DialogTitle>
          <DialogContent>
            <DialogContentText id='alert-dialog-description'>
              Thank you for registering. Please review our Terms of Use.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={gotoStep2} color='primary' autoFocus>
              Continue
            </Button>
          </DialogActions>
        </Dialog>

        {/* Step 2 - T&Cs (paginated) */}
        <Dialog
          open={isShowTermsAndConditions()}
          fullScreen={fullScreen}
          fullWidth
          maxWidth='sm'
          PaperProps={{
            'aria-label': 'Terms of Use',
            sx: {
              height: fullScreen ? '100vh' : 'calc(100vh - 64px)',
              maxHeight: fullScreen ? '100vh' : 'calc(100vh - 64px)',
              '@supports (height: 100dvh)': {
                height: fullScreen ? '100dvh' : 'calc(100dvh - 64px)',
                maxHeight: fullScreen ? '100dvh' : 'calc(100dvh - 64px)',
              },
            },
          }}
        >
          <DialogContent sx={{ p: 0, minHeight: 0, overflow: 'hidden', display: 'flex' }}>
            <PaginatedTerms
              onAgree={() => handleAgreeToTermsAndConditions(true)}
              onDisagree={() => handleAgreeToTermsAndConditions(false)}
              saving={saving}
            />
          </DialogContent>
        </Dialog>
      </GridItem>
    </AuthenticatedContainer>
  );
}
